import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Settings, Webhook } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { usePartnerAccountWithPolling } from '@/api/partner-accounts';
import { useSyncLogs } from '@/api/sync-logs';
import { useWebhookLogs } from '@/api/webhook-logs';
import { useSyncStatusInvalidation } from '@/hooks/useSyncStatusInvalidation';
import { SyncLogsList } from '@/components/partner-accounts/SyncLogsList';
import { WebhookLogsList } from '@/components/partner-accounts/WebhookLogsList';
import { SyncStatusBadge } from '@/components/partner-accounts/SyncStatusBadge';
import { ConnectionStatusBadge } from '@/components/partner-accounts/ConnectionStatusBadge';
import { SyncControlButtons } from '@/components/partner-accounts/SyncControlButtons';
import { FileImportCard } from '@/components/partner-accounts/FileImportCard';
import { SyncComparisonBadge } from '@/components/partner-accounts/SyncComparisonBadge';
import { EnableDisableToggle } from '@/components/partner-accounts/EnableDisableToggle';
import { DisabledAccountBadge } from '@/components/partner-accounts/DisabledAccountBadge';
import { ReconnectAccountDialog } from '@/components/partner-accounts/ReconnectAccountDialog';
import { PartnerAccountSettingsDialog } from '@/components/partner-accounts/PartnerAccountSettingsDialog';
import { TimestampDisplay } from '@/components/ui/timestamp-display';
import { RestrictionChips } from '@/components/layout/RestrictionChips';

/**
 * Format sync interval in human-readable form
 */
function formatSyncInterval(minutes: number): string {
  if (minutes < 60) return `Every ${String(minutes)} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? 'Every hour' : `Every ${String(hours)} hours`;
}

/**
 * Format duration in human-readable form (e.g., "2m 34s")
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0
    ? `${String(minutes)}m ${String(remainingSeconds)}s`
    : `${String(minutes)}m`;
}

export function PartnerAccountDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [isReconnectDialogOpen, setIsReconnectDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  // Auto-polls during active sync, stops when sync completes
  const { data: account, isLoading: accountLoading, error: accountError } = usePartnerAccountWithPolling(id ?? '');
  const { data: syncLogs, isLoading: logsLoading } = useSyncLogs(id ?? '', { limit: 10 });
  const { data: webhookLogs, isLoading: webhookLogsLoading } = useWebhookLogs(id, { limit: 10 });

  // Invalidate sync logs and related queries when sync completes
  useSyncStatusInvalidation(account?.syncStatus, {
    accountId: id,
    invalidateList: true,
  });

  if (accountLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (accountError || !account) {
    return (
      <div className="space-y-6">
        <Link to="/partner-accounts">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Partner Accounts
          </Button>
        </Link>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load partner account. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Link to="/partner-accounts">
            <Button variant="ghost" size="sm" className="-ml-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{account.name}</h1>
            {!account.enabled && <DisabledAccountBadge />}
          </div>
          <p className="text-muted-foreground">{account.partnerType?.name ?? 'Unknown'}</p>
          <RestrictionChips scope="account" partnerTypeCode={account.partnerType?.code} />
        </div>
        <div className="flex items-center gap-2">
          {account.partnerType?.code !== 'file_import' && (
            <SyncControlButtons
              accountId={account.id}
              syncStatus={account.syncStatus}
              connectionStatus={account.connectionStatus}
              disabled={!account.enabled}
            />
          )}
          <EnableDisableToggle
            accountId={account.id}
            accountName={account.name}
            enabled={account.enabled}
          />
        </div>
      </div>

      {/* Connection Error Alert */}
      {account.connectionStatus === 'error' && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-destructive">Connection Error</h3>
              <p className="text-sm text-destructive/80">
                Unable to connect to {account.partnerType?.name ?? 'partner'}. Credentials may have expired.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { setIsReconnectDialogOpen(true); }}
              data-testid="reconnect-btn"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reconnect
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Connection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ConnectionStatusBadge status={account.connectionStatus} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Sync Status</CardTitle>
          </CardHeader>
          <CardContent>
            <SyncStatusBadge status={account.syncStatus} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Last Sync</CardTitle>
          </CardHeader>
          <CardContent>
            <TimestampDisplay timestamp={account.lastSyncAt} className="text-sm" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Next Sync</CardTitle>
          </CardHeader>
          <CardContent>
            <TimestampDisplay timestamp={account.nextSyncAt} className="text-sm" />
          </CardContent>
        </Card>
      </div>

      {/* Account Settings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Account Settings
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setIsSettingsDialogOpen(true); }}
            data-testid="edit-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Edit Settings
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Sync Interval</span>
            <span className="text-sm font-medium">{formatSyncInterval(account.syncIntervalMinutes)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Default Location Owner</span>
            {account.defaultOwner ? (
              <div className="text-right">
                <div className="text-sm font-medium">{account.defaultOwner.name || 'No name'}</div>
                <div className="text-xs text-muted-foreground">{account.defaultOwner.email}</div>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground italic">Not set</span>
            )}
          </div>
          <Separator />
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Auto-Calculate Prices</span>
              <Badge variant={account.pricingFormulaEnabled ? 'default' : 'secondary'}>
                {account.pricingFormulaEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            {account.pricingFormulaEnabled && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Price Multiplier</span>
                  <span className="text-sm font-medium">×{String(account.priceMultiplier ?? 1)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Platform Premium</span>
                  <span className="text-sm font-medium">{String((account.scoutaroundPremium ?? 0) * 100)}%</span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* File Import */}
      {account.partnerType?.code === 'file_import' && id && <FileImportCard accountId={id} />}

      {/* Location Counts */}
      {account.locationCounts && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Synced Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SyncComparisonBadge
                syncedCount={account.locationCounts.total}
                sourceCount={account.sourceListingCount}
                sourceCountUpdatedAt={account.sourceCountUpdatedAt}
                variant="compact"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Enriched</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{String(account.locationCounts.enriched)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Published</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{String(account.locationCounts.published)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sync Statistics */}
      {account.syncStats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Syncs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{account.syncStats.totalSyncs}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Successful
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {account.syncStats.successfulSyncs}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {account.syncStats.failedSyncs}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="sync-avg-duration-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Duration (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {account.syncStats.avgSyncDurationSeconds !== null ? (
                <div>
                  <div className="text-2xl font-bold" data-testid="sync-avg-duration-value">
                    {formatDuration(account.syncStats.avgSyncDurationSeconds)}
                  </div>
                  {account.syncStats.durationRange && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid="sync-duration-range">
                      Range: {formatDuration(account.syncStats.durationRange.min)} - {formatDuration(account.syncStats.durationRange.max)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground" data-testid="sync-avg-duration-insufficient">
                  Need {String(10 - account.syncStats.activitySyncCount)} more syncs
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Metrics */}
      {account.syncStats && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card data-testid="sync-change-rate-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Change Rate (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {account.syncStats.changeRatePercent !== null ? (
                <div>
                  <div className="text-2xl font-bold" data-testid="sync-change-rate-value">
                    {String(account.syncStats.changeRatePercent)}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    of locations changed per sync
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground" data-testid="sync-change-rate-insufficient">
                  Need {String(10 - account.syncStats.activitySyncCount)} more syncs
                </div>
              )}
            </CardContent>
          </Card>
          <Card data-testid="sync-activity-period-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Activity Period
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="sync-activity-count">
                {String(account.syncStats.activitySyncCount)} syncs
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                in last {String(account.syncStats.activityPeriodDays)} days
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sync History */}
      <SyncLogsList
        logs={syncLogs?.data ?? []}
        lastRemovalCheckAt={syncLogs?.lastRemovalCheckAt ?? null}
        isLoading={logsLoading}
      />

      {/* Webhook Status Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Webhook className="h-4 w-4" />
            Webhook Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Registration</span>
            <Badge variant={account.webhookStatus?.registered ? 'success' : 'secondary'}>
              {account.webhookStatus?.registered ? 'Registered' : 'Not Registered'}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Last Event</span>
            <span className="text-sm">
              {account.webhookStatus?.lastEventAt ? (
                <TimestampDisplay timestamp={account.webhookStatus.lastEventAt} />
              ) : (
                <span className="text-muted-foreground">Never</span>
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Events (24h)</span>
            <span className="text-sm font-medium">{String(account.webhookStatus?.last24hCount ?? 0)}</span>
          </div>
          {(account.webhookStatus?.last24hFailed ?? 0) > 0 && (
            <div className="flex justify-between items-center text-destructive">
              <span className="text-sm">Failed (24h)</span>
              <span className="text-sm font-medium">{String(account.webhookStatus?.last24hFailed ?? 0)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Events */}
      <WebhookLogsList
        logs={webhookLogs?.data ?? []}
        isLoading={webhookLogsLoading}
        title="Recent Webhook Events"
        maxItems={10}
      />

      {/* Reconnect Dialog */}
      <ReconnectAccountDialog
        account={account}
        open={isReconnectDialogOpen}
        onOpenChange={setIsReconnectDialogOpen}
      />

      {/* Settings Dialog */}
      <PartnerAccountSettingsDialog
        account={account}
        open={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
        onReconnectClick={() => {
          setIsSettingsDialogOpen(false);
          setIsReconnectDialogOpen(true);
        }}
      />
    </div>
  );
}
